<template>
  <div>
    <ul class="wrap-result-list">
      <li
        v-for="resort in results"
        :key="resort.id"
        class="resort-item relative"
      >
        <div class="resort-item__slider">
          <cabin-image-carousel
            :width="1000"
            v-if="(resort.supplier_gallery || []).length > 0"
            :images="resort.supplier_gallery"
          />
          <image-with-fallback
            v-else
            class="resort-image rounded h-full object-cover"
            :src="resort.supplier_image_url"
            :alt="resort.supplier_name"
          />
        </div>
        <div class="px-8 py-4 sm:px-4">
          <div class="flex text-sm font-medium leading-6 pt-1">
            <svg
              width="20"
              height="20"
              class="text-black-light-5"
              fill="currentColor"
            >
              <use xlink:href="/img/sprite.svg#ic_location"></use>
            </svg>
            <span class="text-black-light-4 mr-1">
              {{ resort.__platform_destination_title }},
              {{ resort.country_name }}
            </span>
          </div>
          <h4 class="fs-20 font-medium lh-130 pt-2 pb-1 text-green">
            {{ resort.supplier_name }}
          </h4>
          <h3 class="fs-24 font-medium lh-130">
            {{ resort.title }}
          </h3>

          <div class="standard-list my-2" v-html="resort.text"></div>
          <div v-if="resort.blocked_from && resort.blocked_to" class="mb-2">
            <i>
              Offers not applicable from
              <time>{{ formatExpiryDate(resort.blocked_from) }}</time> to
              <time>{{ formatExpiryDate(resort.blocked_to) }}</time>
            </i>
          </div>

          <div
            v-if="resort.travel_until && getDaysLeft(resort.travel_until)"
            class="offer-ends mb-2"
          >
            {{ getDaysLeft(resort.travel_until) }}
          </div>

          <b v-if="resort.travel_from && resort.travel_until" class="fs-18"
            >Travel from
            <time>{{ formatExpiryDate(resort.travel_from) }}</time> until
            <time>{{ formatExpiryDate(resort.travel_until) }}</time></b
          >
          <b v-else-if="resort.travel_until" class="fs-18"
            >Travel until
            <time>{{ formatExpiryDate(resort.travel_until) }}</time></b
          >

          <hr class="separator-slim my-6" />

          <div class="flex flex-col items-end">
            <span class="text-black-light-4">
              Starting from
            </span>
            <p
              v-if="resort.original_price"
              class="line-through font-normal text-sm leading-6 aligns-center original-price"
            >
              <currency-context>
                <template v-slot="{ convert }">
                  <span v-text="convert(resort.original_price)"></span>
                </template>
              </currency-context>
              {{ resort.original_price_text }}
            </p>
            <h4
              v-if="resort.new_price"
              class="fs-20 lh-130 font-medium discount-price"
            >
              <currency-context>
                <template v-slot="{ convert }">
                  <span v-text="convert(resort.new_price)"></span>
                </template>
              </currency-context>
              <span class="font-normal text-base">
                {{ resort.new_price_text }}
              </span>
            </h4>
            <span
              class="text-black-light-4 text-right"
              v-html="resort.includes_text"
            ></span>
            <div class="flex mt-2 wrap-action">
              <a :href="getUrl(resort.supplier_url)" class="block">
                <button
                  type="button"
                  class="btn btn-primary btn-outlined input-tall btn-action mr-4"
                >
                  View resort
                </button>
              </a>
              <trengo-link>
                <template v-slot="{ openChat }">
                  <button
                    type="button"
                    class="btn btn-primary flex items-center justify-center btn-action"
                    @click="openChat()"
                  >
                    Chat with us
                  </button>
                </template>
              </trengo-link>
              
            </div>
          </div>
        </div>
      </li>
    </ul>

    <div class="w-fit-content sm:w-full m-auto mt-16 sm:mb-16 flex flex-col">
      <button
        :disabled="isLoading"
        v-if="visibleShowMore"
        @click="$emit('show-more')"
        class="btn-show-more btn btn-outlined btn-secondary w-full flex items-center"
      >
        Load more offers
        <div v-if="isLoading" class="loader ml-2" />
      </button>
    </div>
  </div>
</template>

<script>
import TrengoLink from "../core/TrengoLink";
import CabinImageCarousel from "../../views/liveaboard/CabinImageCarousel";
import ImageWithFallback from "../core/ImageWithFallback";
import CurrencyContext from "../core/CurrencyContext";

export default {
  components: {
    CabinImageCarousel,
    ImageWithFallback,
    CurrencyContext,
    TrengoLink,
  },
  props: {
    datetime: {
      type: Array,
      required: true,
    },
    page: {
      type: Number,
      required: true,
    },
    results: {
      type: Array,
      default: [],
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    count: {
      type: Number,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    visibleShowMore() {
      return this.results.length < this.count;
    },
  },
  methods: {
    getUrl(supplierUrl) {
      if (supplierUrl && !supplierUrl.startsWith("http")) {
        return `//${supplierUrl}`;
      }

      return supplierUrl;
    },
    formatExpiryDate(dateString) {
      return moment(dateString, "YYYY-MM-DD").format("DD MMM YYYY");
    },
    getDaysLeft(date) {
      const diff = moment(date).diff(moment(), "days", true);
      if (diff > 30) {
        return null;
      }

      if (diff < 0) {
        return "Offer ends today";
      }

      const d = Math.ceil(diff);
      const time = d === 1 ? "1 day" : `${d} days`;
      return `Offer ends in ${time}`;
    },
  },
};
</script>

<style scoped>
.btn-action {
  width: 200px;
}

.resort-item {
  display: grid;
  grid-template-columns: 600px auto;
  grid-column-gap: 0.5rem;
  border: 1px solid #0e1010;
  border-radius: 0.5rem;
  margin-bottom: 3.5rem;
}

.standard-list ::v-deep ul {
  padding: 0 0 0 20px;
}

.text-green {
  color: #1695a3 !important;
}

::v-deep .cover-image {
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
}

::v-deep .VueCarousel,
::v-deep .VueCarousel-wrapper,
::v-deep .VueCarousel-inner {
  height: 100% !important;
}

.standard-list ::v-deep ul:not(.list-reset) li {
  margin-bottom: 0px;
}

.btn-show-more {
  width: 250px;
  justify-content: center;
}

.offer-ends {
  width: fit-content;
  background-color: #e32d26;
  padding: 0.5rem 1rem;
  color: white;
  border-radius: 0.5rem;
  margin-top: 0.5rem;
}

@media (min-width: 900px) and (max-width: 1200px) {
  ::v-deep .VueCarousel-wrapper {
    overflow: auto !important;
  }

  ::v-deep .VueCarousel-slide {
    max-width: unset;
  }

  .resort-item {
    grid-template-columns: 450px auto;
  }

  .btn-action {
    width: 160px;
  }
}

@media (max-width: 900px) {
  .resort-item {
    display: flex;
    flex-direction: column;
    grid-column-gap: 0.5rem;
    border: 1px solid #0e1010;
    border-radius: 0.5rem;
    margin-bottom: 3.5rem;
  }

  .btn-action {
    width: 100%;
    margin-top: 8px;
  }

  .btn-show-more {
    width: 100%;
    justify-content: center;
  }

  ::v-deep .VueCarousel-wrapper {
    overflow: hidden !important;
  }

  ::v-deep .VueCarousel-slide {
    max-width: 100% !important;
  }

  ::v-deep .cover-image {
    border-top-right-radius: 0.5rem;
    border-bottom-left-radius: 0;
  }

  .wrap-action {
    flex-direction: column;
    width: 100%;
  }

  .btn-action {
    margin-right: 0px;
  }
}

.loader {
  border: 2px solid #dbdbdb;
  border-radius: 50%;
  border-top: 2px solid #1695a3;
  width: 20px;
  height: 20px;
  animation: spin 2s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
